import { Link } from "react-router-dom";
import {
  FileText,
  Building2,
  Search,
  Calculator,
  Phone,
  LogIn,
  LogOut,
  User,
  BookOpen,
  Shield,
  DollarSign,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { ThemeToggle } from "./ThemeToggle";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { format } from "date-fns";
import { cn } from "@/lib/utils";

interface MobileMenuProps {
  user: any;
  profile: any;
  onSignOut: () => Promise<void>;
  isOpen: boolean;
  onClose: () => void;
}

export function MobileMenu({
  user,
  profile,
  onSignOut,
  isOpen,
  onClose,
}: MobileMenuProps) {
  return (
    <Sheet open={isOpen} onOpenChange={onClose} className="z-50">
      <SheetContent
        side="left"
        className="w-[300px] sm:w-[400px] p-0 border-r border-border dark:border-gray-800"
      >
        <nav className="flex flex-col h-full">
          <SheetHeader className="p-4 border-b border-border dark:border-gray-800">
            <SheetTitle className="flex items-center gap-2">
              <BookOpen className="h-6 w-6 text-primary" />
              <span className="text-xl font-semibold">Numberz Insight</span>
            </SheetTitle>
          </SheetHeader>

          <div className="flex-1 overflow-auto py-4">
            {user && (
              <div className="px-4 mb-6">
                <div className="bg-gradient-to-r from-gray-50/50 to-gray-100/50 dark:from-gray-800/50 dark:to-gray-900/50 backdrop-blur-sm rounded-lg p-4">
                  <div className="flex items-center gap-3">
                    <Avatar className="h-12 w-12 border-2 border-primary/10">
                      <AvatarImage src={profile?.avatar_url} />
                      <AvatarFallback className="bg-primary/5 text-primary font-medium">
                        {profile?.full_name?.charAt(0) ||
                          user.email?.charAt(0).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">
                        {profile?.full_name || "User"}
                      </p>
                      <p className="text-xs text-muted-foreground truncate">
                        {user.email}
                      </p>
                      <div className="mt-1 inline-flex items-center rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-medium text-primary">
                        Client ID: {profile?.client_number}
                      </div>
                      {profile?.last_login && (
                        <p className="text-xs text-muted-foreground mt-1">
                          Last login:{" "}
                          {format(new Date(profile.last_login), "PPp")}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-1 px-3">
              <Link
                to="/abn-registration"
                className={cn(
                  "flex items-center space-x-2 px-4 py-2 text-sm rounded-md transition-colors",
                  "hover:bg-accent hover:text-accent-foreground"
                )}
              >
                <FileText className="w-4 h-4" />
                <span>ABN Registration</span>
              </Link>
              <Link
                to="/gst-registration"
                className={cn(
                  "flex items-center space-x-2 px-4 py-2 text-sm rounded-md transition-colors",
                  "hover:bg-accent hover:text-accent-foreground"
                )}
              >
                <FileText className="w-4 h-4" />
                <span>GST Registration</span>
              </Link>
              <Link
                to="/acn-registration"
                className={cn(
                  "flex items-center space-x-2 px-4 py-2 text-sm rounded-md transition-colors",
                  "hover:bg-accent hover:text-accent-foreground"
                )}
              >
                <FileText className="w-4 h-4" />
                <span>ACN Registration</span>
              </Link>
              <Link
                to="/company-registration"
                className={cn(
                  "flex items-center space-x-2 px-4 py-2 text-sm rounded-md transition-colors",
                  "hover:bg-accent hover:text-accent-foreground"
                )}
              >
                <Building2 className="w-4 h-4" />
                <span>Company Registration</span>
              </Link>
              <Link
                to="/abn-lookup"
                className={cn(
                  "flex items-center space-x-2 px-4 py-2 text-sm rounded-md transition-colors",
                  "hover:bg-accent hover:text-accent-foreground"
                )}
              >
                <Search className="w-4 h-4" />
                <span>ABN Lookup</span>
              </Link>
              <Link
                to="/calculators"
                className={cn(
                  "flex items-center space-x-2 px-4 py-2 text-sm rounded-md transition-colors",
                  "hover:bg-accent hover:text-accent-foreground"
                )}
              >
                <Calculator className="w-4 h-4" />
                <span>Financial Calculators</span>
              </Link>
              <Link
                to="/tax-information"
                className={cn(
                  "flex items-center space-x-2 px-4 py-2 text-sm rounded-md transition-colors",
                  "hover:bg-accent hover:text-accent-foreground"
                )}
              >
                <DollarSign className="w-4 h-4" />
                <span>Tax Information</span>
              </Link>

              <Link
                to="/contact"
                className={cn(
                  "flex items-center space-x-2 px-4 py-2 text-sm rounded-md transition-colors",
                  "hover:bg-accent hover:text-accent-foreground"
                )}
              >
                <Phone className="w-4 h-4" />
                <span>Contact</span>
              </Link>

              <div className="px-4 py-3 mt-2">
                <h3 className="text-sm font-medium text-muted-foreground">
                  Legal
                </h3>
              </div>

              <Link
                to="/privacy"
                className={cn(
                  "flex items-center space-x-2 px-4 py-2 text-sm rounded-md transition-colors",
                  "hover:bg-accent hover:text-accent-foreground"
                )}
              >
                <Shield className="w-4 h-4" />
                <span>Privacy Policy</span>
              </Link>

              <Link
                to="/terms"
                className={cn(
                  "flex items-center space-x-2 px-4 py-2 text-sm rounded-md transition-colors",
                  "hover:bg-accent hover:text-accent-foreground"
                )}
              >
                <FileText className="w-4 h-4" />
                <span>Terms of Service</span>
              </Link>
            </div>
          </div>

          <div className="border-t border-border dark:border-gray-800 p-4 space-y-4 mt-4">
            <div className="bg-muted/50 rounded-lg p-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Theme</span>
                <ThemeToggle />
              </div>
            </div>
            <Separator />
            {user ? (
              <div className="space-y-2">
                <Link
                  to="/dashboard"
                  className={cn(
                    "flex items-center space-x-2 w-full px-4 py-2 text-sm rounded-md transition-colors",
                    "hover:bg-accent hover:text-accent-foreground"
                  )}
                >
                  <User className="w-4 h-4" />
                  <span>Dashboard</span>
                </Link>
                <button
                  onClick={onSignOut}
                  className={cn(
                    "flex items-center space-x-2 w-full px-4 py-2 text-sm rounded-md transition-colors",
                    "text-destructive hover:bg-destructive/10"
                  )}
                >
                  <LogOut className="w-4 h-4" />
                  <span>Sign Out</span>
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                <Link
                  to="/signin"
                  className={cn(
                    "flex items-center space-x-2 px-4 py-2 text-sm rounded-md transition-colors",
                    "hover:bg-accent hover:text-accent-foreground"
                  )}
                >
                  <LogIn className="w-4 h-4" />
                  <span>Sign In</span>
                </Link>
                <Link
                  to="/signup"
                  className={cn(
                    "flex items-center space-x-2 px-4 py-2 text-sm rounded-md transition-colors",
                    "hover:bg-accent hover:text-accent-foreground"
                  )}
                >
                  <User className="w-4 h-4" />
                  <span>Sign Up</span>
                </Link>
              </div>
            )}
          </div>
        </nav>
      </SheetContent>
    </Sheet>
  );
}
